// pages/Series.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Divider,
  Card,
  Select
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BlockOutlined,
  UnlockOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  ExportOutlined,
  PrinterOutlined,
  ShoppingOutlined,
  FileTextOutlined,
  SwapOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;

const Series = () => {
  // Estados
  const [series, setSeries] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentSerie, setCurrentSerie] = useState(null);
  const [form] = Form.useForm();
  const [tableLoading, setTableLoading] = useState(false);
  
  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Cargar datos iniciales
  useEffect(() => {
    fetchSeries();
  }, []);

  const fetchSeries = async () => {
    setTableLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/series`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Normalizar datos booleanos
      const data = response.data.data.map(item => ({
        ...item,
        tieneproduccion: item.tieneproduccion === true || item.tieneproduccion === 1 || item.tieneproduccion === '1',
        ImpresionTickets: item.ImpresionTickets === true || item.ImpresionTickets === 1 || item.ImpresionTickets === '1',
        ExentoTasa: item.ExentoTasa === true || item.ExentoTasa === 1 || item.ExentoTasa === '1',
        TieneReservas: item.TieneReservas === true || item.TieneReservas === 1 || item.TieneReservas === '1',
        tienetraspaso: item.tienetraspaso === true || item.tienetraspaso === 1 || item.tienetraspaso === '1'
      }));
      
      setSeries(data);
      setFilteredData(data);
      
      console.log('✅ Series cargadas:', data);
    } catch (error) {
      message.error('Error al cargar las series');
      console.error('Error fetchSeries:', error);
    } finally {
      setTableLoading(false);
    }
  };

  // Función para búsqueda global
  const handleGlobalSearch = (searchText) => {
    setGlobalSearchText(searchText);
    
    if (!searchText.trim()) {
      setFilteredData(series);
      return;
    }
    
    const lowerSearchText = searchText.toLowerCase().trim();
    
    const filtered = series.filter(record => {
      return Object.keys(record).some(key => {
        const value = record[key];
        
        if (value === null || value === undefined) return false;
        
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(lowerSearchText);
      });
    });
    
    setFilteredData(filtered);
  };

  // Función para obtener el icono según la serie
  const getIconForSerie = (serie) => {
    switch(serie) {
      case 'A': return <FileTextOutlined style={{ color: '#1890ff' }} />;
      case 'B': return <FileTextOutlined style={{ color: '#52c41a' }} />;
      case 'C': return <FileTextOutlined style={{ color: '#fa8c16' }} />;
      case 'R': return <ShoppingOutlined style={{ color: '#722ed1' }} />;
      case 'T': return <PrinterOutlined style={{ color: '#f5222d' }} />;
      case 'S': return <DatabaseOutlined style={{ color: '#13c2c2' }} />;
      default: return <FileTextOutlined />;
    }
  };

  // Función para obtener el color del tag según la serie
  const getColorForSerie = (serie) => {
    switch(serie) {
      case 'A': return 'blue';
      case 'B': return 'green';
      case 'C': return 'orange';
      case 'R': return 'purple';
      case 'T': return 'red';
      case 'S': return 'cyan';
      default: return 'default';
    }
  };  

  // Configuración de columnas
  const columns = [
    {
      title: 'Serie',
      dataIndex: 'serie',
      key: 'serie',
      width: 80,
      sorter: (a, b) => a.serie.localeCompare(b.serie),
      sortOrder: sortedInfo.columnKey === 'serie' && sortedInfo.order,
      render: (serie) => (
        <Tag color={getColorForSerie(serie)} style={{ fontSize: '14px', fontWeight: 'bold' }}>
          {serie}
        </Tag>
      ),
    },
    {
      title: 'Icono',
      key: 'icono',
      width: 60,
      render: (_, record) => getIconForSerie(record.serie),
    },
    {
      title: 'Producción',
      dataIndex: 'tieneproduccion',
      key: 'tieneproduccion',
      width: 100,
      render: (produccion) => (
        <Badge
          status={produccion ? 'success' : 'default'}
          text={produccion ? 'Sí' : 'No'}
        />
      ),
      filters: [
        { text: 'Con Producción', value: true },
        { text: 'Sin Producción', value: false },
      ],
      filteredValue: filteredInfo.tieneproduccion || null,
      onFilter: (value, record) => record.tieneproduccion === value,
    },
    {
      title: 'Reservas',
      dataIndex: 'TieneReservas',
      key: 'TieneReservas',
      width: 100,
      render: (reservas) => (
        <Badge
          status={reservas ? 'processing' : 'default'}
          text={reservas ? 'Sí' : 'No'}
        />
      ),
      filters: [
        { text: 'Con Reservas', value: true },
        { text: 'Sin Reservas', value: false },
      ],
      filteredValue: filteredInfo.TieneReservas || null,
      onFilter: (value, record) => record.TieneReservas === value,
    },
    {
      title: 'Exento Tasa',
      dataIndex: 'ExentoTasa',
      key: 'ExentoTasa',
      width: 100,
      render: (exento) => (
        <Badge
          status={exento ? 'warning' : 'default'}
          text={exento ? 'Sí' : 'No'}
        />
      ),
      filters: [
        { text: 'Exento', value: true },
        { text: 'No Exento', value: false },
      ],
      filteredValue: filteredInfo.ExentoTasa || null,
      onFilter: (value, record) => record.ExentoTasa === value,
    },
    {
      title: 'Traspaso',
      dataIndex: 'tienetraspaso',
      key: 'tienetraspaso',
      width: 100,
      render: (traspaso) => (
        <Badge
          status={traspaso ? 'processing' : 'default'}
          text={traspaso ? 'Sí' : 'No'}
        />
      ),
      filters: [
        { text: 'Con Traspaso', value: true },
        { text: 'Sin Traspaso', value: false },
      ],
      filteredValue: filteredInfo.tienetraspaso || null,
      onFilter: (value, record) => record.tienetraspaso === value,
    },
    {
      title: 'Contabilidad',
      dataIndex: 'SerieContabilidad',
      key: 'SerieContabilidad',
      width: 100,
      render: (contabilidad) => (
        <Tag color="blue">{contabilidad || '-'}</Tag>
      ),
    },
    {
      title: 'Tickets',
      dataIndex: 'ImpresionTickets',
      key: 'ImpresionTickets',
      width: 100,
      render: (tickets) => (
        <Badge
          status={tickets ? 'processing' : 'default'}
          text={tickets ? 'Sí' : 'No'}
        />
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar serie?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.serie)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
          
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Handlers para las acciones
  const handleCreate = () => {
    setModalType('create');
    setCurrentSerie(null);
    form.resetFields();
    form.setFieldsValue({
      serie: '',
      tieneproduccion: true,
      ImpresionTickets: false,
      ExentoTasa: false,
      TieneReservas: true,
      SerieContabilidad: 'F',
      tienetraspaso: false
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentSerie(record);
    
    form.setFieldsValue({
      ...record,
      tieneproduccion: Boolean(record.tieneproduccion),
      ImpresionTickets: record.ImpresionTickets || false,
      ExentoTasa: record.ExentoTasa || false,
      TieneReservas: record.TieneReservas || false,
      SerieContabilidad: record.SerieContabilidad || 'F',
      tienetraspaso: record.tienetraspaso || false
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Detalle de Serie: ${record.serie}`,
      width: 700,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <strong>Serie:</strong> <Tag color={getColorForSerie(record.serie)} style={{ fontSize: '16px', fontWeight: 'bold' }}>
                {record.serie}
              </Tag>
            </Col>
            <Col span={6}>
              <strong>Icono:</strong> {getIconForSerie(record.serie)}
            </Col>
            
            <Col span={24}>
              <Divider orientation="left">Configuración</Divider>
            </Col>
            
            <Col span={12}>
              <Card size="small" title="Funcionalidades" style={{ height: '100%' }}>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <Space>
                      {record.tieneproduccion ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                      <Text>Producción</Text>
                    </Space>
                  </Col>
                  <Col span={12}>
                    <Space>
                      {record.TieneReservas ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                      <Text>Reservas</Text>
                    </Space>
                  </Col>
                  <Col span={12}>
                    <Space>
                      {record.ImpresionTickets ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                      <Text>Tickets</Text>
                    </Space>
                  </Col>
                  <Col span={12}>
                    <Space>
                      {record.ExentoTasa ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                      <Text>Exento Tasa</Text>
                    </Space>
                  </Col>
                  <Col span={12}>
                    <Space>
                      {record.tienetraspaso ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                      <Text>Traspaso</Text>
                    </Space>
                  </Col>
                </Row>
              </Card>
            </Col>
            
            <Col span={12}>
              <Card size="small" title="Información Contable" style={{ height: '100%' }}>
                <Row gutter={[8, 8]}>
                  <Col span={24}>
                    <strong>Serie Contabilidad:</strong> 
                    <Tag color="blue" style={{ marginLeft: 8 }}>{record.SerieContabilidad || 'F'}</Tag>
                  </Col>
                  <Col span={24}>
                    <Divider style={{ margin: '8px 0' }} />
                  </Col>
                  <Col span={24}>
                    <Text type="secondary">
                      Esta serie se utiliza para {record.TieneReservas ? 'reservas' : 'documentos'} y 
                      {record.tieneproduccion ? ' tiene producción' : ' no tiene producción'}.
                    </Text>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (serie) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${BASE_URL}/api/series/${serie}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const nuevasSeries = series.filter(t => t.serie !== serie);
      setSeries(nuevasSeries);
      
      const nuevoFilteredData = filteredData.filter(t => t.serie !== serie);
      setFilteredData(nuevoFilteredData);
      
      message.success('Serie eliminada correctamente');
      
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar la serie');
      console.error('Error handleDelete:', error);
    } finally {
      setTableLoading(false);
    }
  };

    const handleSave = async () => {
    try {
        setLoading(true);
        const values = await form.validateFields();
        const token = localStorage.getItem('token');
        
        console.log('📝 Valores del formulario:', values);
        
        if (modalType === 'create') {
        // Crear nueva serie
        await axios.post(
            `${BASE_URL}/api/series`,
            values,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        
        message.success('Serie creada correctamente');
        
        } else {
        // Actualizar serie existente
        await axios.put(
            `${BASE_URL}/api/series/${currentSerie.serie}`,
            values,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        
        message.success('Serie actualizada correctamente');
        }
        
        // Cerrar modal
        setModalVisible(false);
        form.resetFields();
        
        // ⭐⭐ SOLUCIÓN: Volver a cargar los datos desde el servidor ⭐⭐
        await fetchSeries();
        
    } catch (error) {
        console.error('❌ Error en handleSave:', error);
        
        if (error.response?.data?.error) {
        message.error(error.response.data.error);
        } else if (error.response?.data?.details) {
        message.error(error.response.data.details);
        } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
        } else {
        message.error('Error al guardar los datos');
        }
    } finally {
        setLoading(false);
    }
    };

  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    let result = [...series];
    
    Object.keys(filters).forEach(key => {
      const filterValues = filters[key];
      if (filterValues && filterValues.length > 0) {
        result = result.filter(item => {
          const itemValue = item[key];
          
          if (key === 'tieneproduccion' || key === 'TieneReservas' || key === 'ExentoTasa' || key === 'tienetraspaso') {
            return filterValues.includes(itemValue);
          }
          
          return true;
        });
      }
    });
    
    if (sorter.field && sorter.order) {
      result.sort((a, b) => {
        const aVal = a[sorter.field] || '';
        const bVal = b[sorter.field] || '';
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          if (sorter.order === 'ascend') {
            return aVal.localeCompare(bVal);
          } else {
            return bVal.localeCompare(aVal);
          }
        } else {
          if (sorter.order === 'ascend') {
            return (aVal || 0) - (bVal || 0);
          } else {
            return (bVal || 0) - (aVal || 0);
          }
        }
      });
    }
    
    if (globalSearchText) {
      const lowerSearchText = globalSearchText.toLowerCase().trim();
      result = result.filter(record => {
        return Object.keys(record).some(key => {
          const value = record[key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(lowerSearchText);
        });
      });
    }
    
    setFilteredData(result);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setGlobalSearchText('');
    setFilteredData(series);
    message.success('Filtros y búsqueda limpiados');
  };

  // Función para exportar
  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={4}>
        <Statistic 
          title="Total Series" 
          value={series.length}
          prefix={<FileTextOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={4}>
        <Statistic 
          title="Con Producción" 
          value={series.filter(t => t.tieneproduccion).length}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={4}>
        <Statistic 
          title="Con Reservas" 
          value={series.filter(t => t.TieneReservas).length}
          prefix={<ShoppingOutlined />}
          valueStyle={{ color: '#fa8c16' }}
        />
      </Col>
      <Col span={4}>
        <Statistic 
          title="Exentas Tasa" 
          value={series.filter(t => t.ExentoTasa).length}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
      <Col span={4}>
        <Statistic 
          title="Con Traspaso" 
          value={series.filter(t => t.tienetraspaso).length}
          prefix={<SwapOutlined />}
          valueStyle={{ color: '#13c2c2' }}
        />
      </Col>
      <Col span={4}>
        <Statistic 
          title="Con Tickets" 
          value={series.filter(t => t.ImpresionTickets).length}
          prefix={<PrinterOutlined />}
          valueStyle={{ color: '#f5222d' }}
        />
      </Col>
    </Row>
  );

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input.Search
          placeholder="Buscar por serie, contabilidad..."
          value={globalSearchText}
          style={{ width: 400 }}
          allowClear
          enterButton
          onChange={(e) => {
            setGlobalSearchText(e.target.value);
            if (e.target.value === '') {
              handleGlobalSearch('');
            }
          }}
          onSearch={handleGlobalSearch}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={clearFilters}
          size="small"
        >
          Limpiar
        </Button>
        <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
          <strong>{filteredData.length}</strong> registros
        </Tag>
      </Space>
    </div>
  );

  return (
    <>
      <PageContainer
        title="Gestión de Series"
        subtitle={`${series.length} series registradas`}
        icon={<FileTextOutlined />}
        loading={tableLoading}
        onRefresh={fetchSeries}
        onCreate={handleCreate}
        onExport={handleExport}
        badgeCount={series.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{series.length}</strong> series
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}

        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="serie"
          loading={tableLoading}
          onChange={handleChange}
          scroll={{ x: 1500 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar */}
      <Modal
        title={modalType === 'create' ? 'Nueva Serie' : 'Editar Serie'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={700}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="serie"
                label="Código de Serie"
                rules={[
                  { required: true, message: 'Por favor ingrese el código de serie' },
                  { max: 10, message: 'Máximo 10 caracteres' }
                ]}
              >
                <Input 
                  placeholder="Ej: A, B, C..." 
                  maxLength={10}
                  disabled={modalType === 'edit'}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="SerieContabilidad"
                label="Serie Contabilidad"
                rules={[{ max: 10, message: 'Máximo 10 caracteres' }]}
              >
                <Input 
                  placeholder="Ej: F" 
                  maxLength={10}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Configuración</Divider>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="tieneproduccion"
                label="Tiene Producción"
                valuePropName="checked"
              >
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="TieneReservas"
                label="Tiene Reservas"
                valuePropName="checked"
              >
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="ExentoTasa"
                label="Exento de Tasa"
                valuePropName="checked"
              >
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="ImpresionTickets"
                label="Impresión de Tickets"
                valuePropName="checked"
              >
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="tienetraspaso"
                label="Tiene Traspaso"
                valuePropName="checked"
              >
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 6 }}>
            <Text type="secondary">
              <strong>💡 Información:</strong> Las series definen los tipos de documentos del sistema 
              (facturas, reservas, tickets) y sus características específicas.
            </Text>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default Series;